import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { translate } from "shared/languages";
import {
  UpdatePayrollSettingsForm,
  UpsertClassSettingForm,
  DuplicateClassSettingForm,
  UpsertClassTiersForm,
  PayrollClassTier,
  UpsertPayrollRateForm,
  RecurringItemForm,
  GenerateRunForm,
  PayrollRun,
  PayrollRunListItem,
  UpdateLineSalaryForm,
  UpdateStaffBaseSalaryForm,
  AddLineItemForm,
  UpdateLineItemForm,
  DraftApplyChoice,
} from "shared/types";
import { ResponseHandler } from "shared/types/axios";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import {
  updatePayrollSettings,
  cancelPendingPayrollSettings,
  upsertClassCommissionSetting,
  upsertClassCommissionTiers,
  duplicateClassCommissionSetting,
  upsertPayrollRate,
  deletePayrollRate,
  createPayrollRecurringItem,
  updatePayrollRecurringItem,
  deletePayrollRecurringItem,
  generatePayrollRun,
  updatePayrollLineSalary,
  updateStaffBaseSalary,
  addPayrollLineItem,
  updatePayrollLineItem,
  deletePayrollLineItem,
  deletePayrollRun,
  finalizePayrollRun,
  reopenPayrollRun,
  recomputePayrollRun,
} from "hooks/api/payroll";

// Bare-key invalidation (not the full tuple) so every cached payroll query
// variant refetches. Payroll never creates notifications server-side, so
// (unlike schedule-v2.ts) these mutations do NOT invalidate ["notification"].
// Returns the invalidation promise (invalidateQueries resolves once the
// resulting refetch actually settles, not just once it's kicked off) — every
// onSuccess below MUST `return` this, not just call it. Otherwise mutateAsync
// resolves as soon as the write completes, before the refetch that
// repopulates the cache has landed; a caller that clears local optimistic
// state right after an awaited mutateAsync (e.g. ClassCommissionEditor's
// doSave) would then briefly render with neither the local draft nor the
// fresh server data — the just-saved row visibly disappearing for a beat.
const bumpPayroll = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ["payroll"] });

// The server answers a frequency change it cannot time on its own (the
// studio has runs, but none for this month) with effective_month_required
// and the two dates to choose between. That one is the form's to handle, so
// it must not raise a toast; every other failure still does.
export const EFFECTIVE_MONTH_REQUIRED = "payrollPage.errors.effective_month_required";

export const useUpdatePayrollSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: UpdatePayrollSettingsForm) => updatePayrollSettings(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.settings.card_title")} ${translate("messages.success.update")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const message = String((error.response?.data as { message?: string })?.message ?? "");
      if (message.includes(EFFECTIVE_MONTH_REQUIRED)) return;
      getApiErrorMessage(error);
    },
  });
};

export const useCancelPendingPayrollSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelPendingPayrollSettings(),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.settings.card_title")} ${translate("messages.success.update")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// No hook-level success toast: this is only ever called as one step of the
// class page's combined "Simpan" (see ClassCommissionPage.tsx's doSave),
// which shows its own single success message once the whole save — setting,
// tiers, and every instructor row — has gone through. A toast per step would
// mean up to three (or, before this was fixed, one PER INSTRUCTOR ROW)
// confirmations for what the user experiences as one click.
export const useUpsertClassCommissionSetting = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: UpsertClassSettingForm & { classTypeId: string }) => upsertClassCommissionSetting(form),
    onSuccess: () => bumpPayroll(qc),
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// No hook-level success toast — see useUpsertClassCommissionSetting above.
export const useUpsertClassCommissionTiers = () => {
  const qc = useQueryClient();
  return useMutation<PayrollClassTier[], AxiosError<ErrorResponse>, UpsertClassTiersForm & { classTypeId: string }>({
    mutationFn: (form: UpsertClassTiersForm & { classTypeId: string }) => upsertClassCommissionTiers(form),
    onSuccess: () => bumpPayroll(qc),
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useDuplicateClassCommissionSetting = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: DuplicateClassSettingForm & { classTypeId: string }) => duplicateClassCommissionSetting(form),
    onSuccess: () => {
      getApiSuccessMessage(translate("payrollPage.rates.duplicate_success"));
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// No hook-level success toast — see useUpsertClassCommissionSetting above.
// doSave calls this once PER changed instructor row via Promise.all, so a
// toast here would fire once per row, not once per Simpan click.
export const useUpsertPayrollRate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: UpsertPayrollRateForm) => upsertPayrollRate(form),
    onSuccess: () => bumpPayroll(qc),
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useDeletePayrollRate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePayrollRate(id),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.rates.name")} ${translate("messages.success.delete")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useCreatePayrollRecurringItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: RecurringItemForm) => createPayrollRecurringItem(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.recurring.name")} ${translate("messages.success.create")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useUpdatePayrollRecurringItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: RecurringItemForm & { id: string }) => updatePayrollRecurringItem(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.recurring.name")} ${translate("messages.success.update")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useDeletePayrollRecurringItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, apply_to }: { id: string; apply_to?: DraftApplyChoice }) => deletePayrollRecurringItem(id, apply_to),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.recurring.name")} ${translate("messages.success.delete")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// No hook-level onError toast on purpose — RunPayrollStartPage intercepts
// this mutation's two known server-rejection messages itself (a richer,
// actionable Alert instead of a bare toast) and falls back to
// getApiErrorMessage for anything else. Every other payroll mutation keeps
// the standard onError. TError is explicitly typed as AxiosError<ResponseHandler>
// (rather than left to infer as the default `Error`) so the caller's
// call-site onError override type-checks against this hook's mutate() AND
// can actually read error.response?.data?.message — react-router-dom's
// ErrorResponse (the type every other mutation here uses) has no `message`
// field, so it only works as a pass-through badge into getApiErrorMessage,
// not for direct access like this override needs.
export const useGeneratePayrollRun = () => {
  const qc = useQueryClient();
  return useMutation<PayrollRun, AxiosError<ResponseHandler>, GenerateRunForm>({
    mutationFn: (form: GenerateRunForm) => generatePayrollRun(form),
    onSuccess: () => bumpPayroll(qc),
  });
};

export const useUpdatePayrollLineSalary = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: UpdateLineSalaryForm) => updatePayrollLineSalary(form),
    onSuccess: () => bumpPayroll(qc),
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// Unlike useUpdatePayrollLineSalary (an ephemeral in-context edit on the run
// table), this is a settings-page save — same category as
// useUpdatePayrollRecurringItem, which already toasts, so this does too.
export const useUpdateStaffBaseSalary = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: UpdateStaffBaseSalaryForm) => updateStaffBaseSalary(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.baseSalary.name")} ${translate("messages.success.update")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useAddPayrollLineItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: AddLineItemForm) => addPayrollLineItem(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.run.item_name")} ${translate("messages.success.create")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useUpdatePayrollLineItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: UpdateLineItemForm) => updatePayrollLineItem(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.run.item_name")} ${translate("messages.success.update")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useDeletePayrollLineItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: { runId: string; lineId: string; itemId: string }) => deletePayrollLineItem(form),
    onSuccess: () => {
      getApiSuccessMessage(`${translate("payrollPage.run.item_name")} ${translate("messages.success.delete")}`);
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useFinalizePayrollRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => finalizePayrollRun(runId),
    onSuccess: () => {
      getApiSuccessMessage(translate("payrollPage.run.finalize_success"));
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useDeletePayrollRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => deletePayrollRun(runId),
    onSuccess: (_data, runId) => {
      getApiSuccessMessage(translate("payrollPage.run.delete_draft_success"));
      // Drop the deleted run (and its lines and payslips) from the cache
      // first, so nothing refetches it, and don't await the rest: awaiting
      // made the button wait while the open run page reloaded the deleted
      // run, got a 404 and retried it three times (~7 s) before moving on.
      qc.removeQueries({ queryKey: ["payroll", "runs", runId] });
      // Riwayat opens next: take the row out of the cached list now so it
      // doesn't flash before the refetch lands.
      qc.setQueryData<PayrollRunListItem[]>(["payroll", "runs"], (runs) => runs?.filter((r) => r.id !== runId));
      void bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useReopenPayrollRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => reopenPayrollRun(runId),
    onSuccess: () => {
      getApiSuccessMessage(translate("payrollPage.run.reopen_success"));
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useRecomputePayrollRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => recomputePayrollRun(runId),
    onSuccess: () => {
      getApiSuccessMessage(translate("payrollPage.run.recompute_success"));
      return bumpPayroll(qc);
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};
